import { module } from 'modujs';
import gsap from 'gsap'
import { lazyLoadImage, animDepixelate } from '../utils/image';

export default class extends module {
    constructor(m) {
        super(m);

        // ==========================================================================
        // ACCORDION SETTINGS
        // ==========================================================================
        this.$toggler = this.$('toggler')[0]
        this.$content = this.$('content')[0]
        this.$contentInner = this.$('content-inner')[0]

        // Accordion List ID
        this.list = this.getData('list')

        // Animation settings
        this.animationOpenDuration = 0.5;
        this.animationCloseDuration = 0.3;

        // ==========================================================================
        // PREVIEW SETTINGS
        // ==========================================================================
        this.$preview = this.$('preview')[0]
        this.$previewTarget = this.$('preview-target')[0]

        // ==========================================================================
        // MISC
        // ==========================================================================
        this.images = Array.from(this.$('image'))

        // Events
        this.events = {
            click: {
                toggler: 'toggle'
            }
        }

        this.compute()
    }

    init() {
        this.computeBind = this.compute.bind(this)
        window.addEventListener('resize', this.computeBind)

        // HOVER HANDLING
        this.touchStartBind = () => {
            this.isTouch = true;
        }
        this.el.addEventListener('touchstart', this.touchStartBind, { once: true })

        this.enter = (e) => {
            if(this.isTouch) return
            if(!this.$preview) return

            const img = this.$preview.querySelector('img')

            if(!this.$preview.classList.contains('-loaded')) {
                lazyLoadImage(img, null, () => {
                    animDepixelate(img)
                })
            } else {
                animDepixelate(img)
            }
        }
        this.el.addEventListener('mouseenter', this.enter)
        this.el.addEventListener('focusin', this.enter)
    }

    /**
     * Toggler
     *
     * @param item {node}
     */
    toggle(e) {
        if(this.isOpened()) {
            this.close()
        } else {
            this.open()
        }
    }

    isOpened() {
        return this.el.classList.contains('is-opened');
    }

    compute() {
        this.isMobile = window.innerWidth < 700
        this.canHover = window.matchMedia("(hover: hover)").matches

        this.canAnimatePreview = this.canHover && !this.isMobile;
    }

    /**
     * open
     *
     * @param parentNode item {node}
     */
    open() {
        const previousActiveItem = {
            el: document.body.querySelector('[data-module-work-list-item].is-opened'),
        }
        if(previousActiveItem.el) {
            previousActiveItem.content = this.$('content', previousActiveItem.el)[0];
            previousActiveItem.contentBCR = previousActiveItem.content.getBoundingClientRect();
        }

        this.call('close', this.list, 'WorkListItem')

        // Update UI
        this.$toggler.setAttribute('aria-expanded', 'true')
        if(this.getData('label-close', this.$toggler)) this.$toggler.children[0].innerText = this.getData('label-close', this.$toggler)

        // Reset view
        this.el.classList.add('is-opened');
        gsap.set(this.$content, { display: 'block' })

        //// HANDLE SCROLLTO
        // Compute offset needed to "center" item
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) * parseFloat(getComputedStyle(document.documentElement).fontSize);
        const innerBCR = this.$contentInner.getBoundingClientRect();
        let offset = -headerHeight;
        if(this.el.previousElementSibling != previousActiveItem.el && previousActiveItem.contentBCR && previousActiveItem.contentBCR.top < innerBCR.top) offset-=previousActiveItem.contentBCR.height

        // Do the scrollTo
        this.call('scrollTo', { target: this.el.previousElementSibling ? this.el.previousElementSibling : this.el, options: { offset, duration: this.animationOpenDuration } }, 'Scroll')

        //// HANDLE LAZY LOADING
        // Start lazy loading inner images
        for(let image of [...this.images,this.$previewTarget]) {
            if (!image) continue
            let img = image.querySelector('img');
            lazyLoadImage(img, null, () => {
                if(this.canAnimatePreview) { // Don't depixelate preview target on desktop to prevent weird glitch after preview animation
                    if(image.dataset.workListItem != 'preview-target')
                        animDepixelate(img)
                } else {
                    animDepixelate(img)
                }
            })
        }

        //// HANDLE PREVIEW ANIMATION
        if(this.tw && this.tw.kill) this.tw.kill()

        // Compute preview related elements' dimensions
        gsap.set(this.$preview, { clearProps: 'all' })
        if(this.canAnimatePreview) {
            gsap.set(this.$preview, { opacity: 1 })
            gsap.set(this.$previewTarget, { opacity: 0 })
        }

        const previewBCR = this.$preview.getBoundingClientRect();
        const previewTargetBCR = this.$previewTarget.getBoundingClientRect();
        const previewOffset = {
            top: previewTargetBCR.top - previewBCR.top + (previewBCR.height/2),
            left: previewTargetBCR.left - previewBCR.left,
            scale: previewTargetBCR.width / previewBCR.width
        }

        // console.log(previewBCR,previewTargetBCR,previewOffset);

        // Actual animation timeline
        this.tw = gsap.timeline({
            onComplete: () => {
                if(this.canAnimatePreview) {
                    gsap.set([this.$preview, this.$previewTarget], { clearProps: 'all' })
                    gsap.set(this.$preview, { opacity: 0 })
                }
            }
        })
        if(this.canAnimatePreview) {
            this.tw.to(this.$preview, {
                duration: this.animationOpenDuration,
                x: previewOffset.left,
                y: previewOffset.top,
                scale: previewOffset.scale,
                ease: 'power4.out',
                force3D: true
            }, 0)
        }
        this.tw.fromTo(this.$content, { opacity: 0 }, {
            duration: this.animationOpenDuration*2/3,
            opacity: 1
        }, this.animationOpenDuration*1/3)
        this.tw.to(this.$content, {
            duration: this.animationOpenDuration,
            height: innerBCR.height
        }, 0)

        if(window.gtag) {
            gtag("event", "click", {
                "event_category": "work_accordions",
                "event_label": 'open'
            });
        }
    }

    /**
     * Close
     *
     * @param
     */
    close(list = undefined) {
        if(list && list != this.list || !this.el.classList.contains('is-opened')) return;

        this.$toggler.setAttribute('aria-expanded', 'false')
        if(this.getData('label-open', this.$toggler)) this.$toggler.children[0].innerText = this.getData('label-open', this.$toggler)

        this.el.classList.remove('is-opened');

        if(this.tw && this.tw.kill) this.tw.kill()

        // COMPUTE PREVIEW
        gsap.set(this.$preview, { clearProps: 'all' }, 0)
        if(this.canAnimatePreview)
            gsap.set(this.$previewTarget, { opacity: 0 })
        const previewBCR = this.$preview.getBoundingClientRect();
        const previewTargetBCR = this.$previewTarget.getBoundingClientRect();
        const previewOffset = {
            top: previewTargetBCR.top - previewBCR.top + (previewBCR.height/2),
            left: previewTargetBCR.left - previewBCR.left,
            scale: previewTargetBCR.width / previewBCR.width
        }

        this.tw = gsap.timeline({
            onComplete: () => {
                gsap.set(this.$content, { clearProps: 'display' })

                if(this.canAnimatePreview)
                    gsap.set([this.$preview, this.$previewTarget], { clearProps: 'all' })
            }
        })
        if(this.canAnimatePreview) {
            this.tw.from(this.$preview, {
                duration: this.animationCloseDuration,
                x: previewOffset.left,
                y: previewOffset.top,
                scale: previewOffset.scale,
                ease: 'power4.out',
                force3D: true
            }, 0)
        }
        this.tw.to(this.$content, {
            duration: this.animationCloseDuration,
            height: 0,
            opacity: 0
        }, 0)
    }

    inview(e) {
        if(e.target == this.el) {
            // Handle mobile lazy loading relative to scroll position
            if(!this.canAnimatePreview && this.isMobile && !this.$preview.classList.contains('-loaded')) {
                let img = this.$preview.querySelector('img');
                lazyLoadImage(img, null, () => {
                    if(this.canAnimatePreview) {
                        if(image.dataset.workListItem != 'preview-target')
                            animDepixelate(img)
                    } else {
                        animDepixelate(img)
                    }
                })
            }
        }
    }

    destroy() {
        window.removeEventListener('resize', this.computeBind)

        this.el.removeEventListener('mouseenter', this.enter)
        this.el.removeEventListener('focusin', this.enter)
    }
}
